import { cn } from 'helpers/classnames';
import type { AirplaneButtonDimension, AirplaneCacheDimensionOption, SetItemSelection } from 'types/AirplaneCache';
import { capitalize } from 'helpers/index';
import useMartyContext from 'hooks/useMartyContext';
import { FEATURE_PDP_OOS_UX } from 'constants/features';
import { useIsEnrolledInVariant } from 'hooks/unleash/useIsEnrolledInVariant';
import { isSizeOption } from 'utils/airplaneCache';
import BellSmallIcon from 'components/common/IconsV2/BellSmallIcon';

import css from 'styles/components/productdetail/airplaneButton.scss';

interface Props {
  isSelected: boolean;
  isInStock: boolean;
  option: Pick<AirplaneCacheDimensionOption, 'constraintValue' | 'label'>;
  radioButtonName: string;
  setItemSelection: SetItemSelection;
  typeKey: AirplaneButtonDimension;
  namespace: string;
}

/**
 * Code search keywords:
 * airplaneSeatGender, airplaneSeatSize, airplaneSeatWidth,
 * airplaneSeatGenderOos, airplaneSeatSizeOos, airplaneSeatWidthOos
 */

export default function AirplaneButton(props: Props) {
  const { isSelected, isInStock, option, radioButtonName, setItemSelection, typeKey, namespace } = props;

  const {
    testId,
    marketplace: { is6pm }
  } = useMartyContext();
  const { isEnrolled: isPdpOosUx } = useIsEnrolledInVariant(FEATURE_PDP_OOS_UX);

  const { constraintValue, label } = option;

  const ariaLabel = makeAriaLabel(label, isInStock, typeKey);
  const dimensionNameText = capitalize(typeKey);
  const oosText = isInStock ? '' : 'Oos';

  const inputId = `radio-${constraintValue}-${namespace}`;
  const inputTestId = testId(`airplaneSeat${dimensionNameText}${oosText}Radio`);

  const labelTestId = testId(`airplaneSeat${dimensionNameText}${oosText}`);

  const containerClasses = cn(
    css.container,
    {
      [css.selected]: isSelected,
      [`${css.oos} ${css.optionButton}`]: !isInStock && !isPdpOosUx,
      [`${css.oosUx} ${css.optionButton}`]: !isInStock && isPdpOosUx,
      [css.optionButtonSelected]: !isInStock && isSelected
    },
    css.optionButtonContainer
  );
  return (
    <div className={containerClasses}>
      {!is6pm && isPdpOosUx && !isInStock && isSizeOption(radioButtonName) && (
        <div className="absolute right-1 top-1 z-[2]">
          <BellSmallIcon size={16} className={cn('h-3 w-3 [&>path]:stroke-[1.5]', { ['text-reversed']: isSelected })} />
        </div>
      )}
      <input
        type="radio"
        id={inputId}
        data-test-id={inputTestId}
        aria-label={ariaLabel}
        name={radioButtonName}
        value={constraintValue}
        checked={isSelected}
        data-label={label}
        data-track-label={typeKey}
        onChange={setItemSelection}
      />
      <label htmlFor={inputId} data-test-id={labelTestId}>
        <span
          className={cn(
            {
              [css.labelOos]: isPdpOosUx && !isInStock,
              '[&::after]:border-reversed': isPdpOosUx && isSelected
            },
            'zappos:normal-case'
          )}
        >
          {label}
        </span>
      </label>
    </div>
  );
}

const BUTTON_ARIA_LABEL_PREFIXES: Record<AirplaneButtonDimension, string> = {
  ageGroup: 'Age Group',
  gender: 'Gender',
  singleShoeSide: 'Side',
  size: 'Size',
  sizeRange: 'Size Range',
  width: 'Width'
};

function makeAriaLabel(optionLabel: string, isInStock: boolean, typeKey: AirplaneButtonDimension) {
  const dimensionName = BUTTON_ARIA_LABEL_PREFIXES[typeKey];
  const parts = [dimensionName, optionLabel];
  if (!isInStock) {
    parts.push('is Out of Stock');
  }
  return parts.join(' ');
}
